
import React, { useState, useEffect, useMemo, useCallback, Suspense, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast, ToastContainer } from 'react-toastify';
import { MetaGridAction } from '../../Redux/slices';
import { GET_METADATA, POST_METADATA_BULK, Metadataupdate_URL } from '../../service/API_URL';
import { getInstantData, postInstantData, deleteInstantData } from '../../api/apiMethod';
import { permissionDetails } from '../../utility/localStoageData';
import { ContentGroup } from '../../service/Constant';
import backicon from '../../assets/svg/metaEdit/arrow-left.svg';
import styles from './editable-grid.module.scss';
import * as XLSX from 'xlsx';
import SearchBar from './SearchBar';
import AddRowButton from './AddRowButton';
import DeleteRowButton from './DeleteRowButton';
import ExportButton from './ExportButton';
import UpdateButton from './UpdateButton';
import LoadingSpinner from './LoadingSpinner';
import NoDataMessage from './NoDataMessage';
import { getColumns } from './columns';
import { fi } from 'date-fns/locale';
import moment from 'moment';

const EditableTable = React.lazy(() => import('../../component/EditableTable'));

function EditableGrid({ data }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const locationState = useLocation();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [displayedRows, setDisplayedRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [locationData, setLocationData] = useState({ state: null });
  const [permission, setPermission] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [page, setPage] = useState(0);
  const [highlightedRowId, setHighlightedRowId] = useState(null);
  const rowsPerPage = 50;
  const isAddingRow = useRef(false);


  const { AudioLanguageList, MaturityList, CountryList } = useSelector((state) => ({
    AudioLanguageList: state.AudioLanguageList?.data,
    MaturityList: state.MaturityList?.data,
    CountryList: state.CountryList?.data?.data,

  }));

  const { GenreList, ThemesList, CategoriesList } = useSelector((state) => ({
    GenreList: state.GenreList,
    ThemesList: state.ThemesList,
    CategoriesList: state.CategoriesList,
  }));


  useEffect(() => {
    console.log("locationState:", locationState);
    setPermission(permissionDetails().includes('ASSET_MANAGEMENT_EDIT'));
    const AssetPartnerDetail = JSON.parse(localStorage.getItem("AssetPartnerDetail"));
    const partnerId = AssetPartnerDetail?.id || 1;
    console.log("Selected partnerId:", partnerId, "Source:", AssetPartnerDetail?.id ? "AssetPartnerDetail" : "Default");
    if (partnerId) {
      setLocationData({
        state: {
          id: partnerId,
          name: AssetPartnerDetail?.name || locationState?.state?.name || "Unknown Partner"
        }
      });
      getMetaData(partnerId);
    } else {
      console.warn("No partnerId found");
      toast.error("No partner selected. Please select a partner and try again.", {
        position: "bottom-right",
        autoClose: 5000,
      });
      setLoading(false);
    }
  }, [locationState]);


  useEffect(() => {
    if (locationData?.state?.id) {
      console.log('Fetching metadata for partnerId:', locationData.state.id);
      getMetaData(locationData.state.id);
    } else {
      console.log('No partnerId in locationData');
    }
  }, [locationData]);

  useEffect(() => {
    const validRows = rows.filter(row => row != null && typeof row === 'object' && row.programId !== undefined);
    if (searchText.length >= 2) {
      const filtered = validRows.filter(row =>
        (row.programId && row.programId.toString().toLowerCase().includes(searchText.toLowerCase())) ||
        (row.title && row.title.toLowerCase().includes(searchText.toLowerCase()))
      );
      setFilteredRows(filtered);
      setDisplayedRows(filtered.slice(0, rowsPerPage));
      setPage(0);
    } else {
      setFilteredRows(validRows);
      setDisplayedRows(validRows.slice(0, rowsPerPage));
    }
  }, [searchText, rows]);

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom && !loading && displayedRows.length < filteredRows.length) {
      loadMoreRows();
    }
  };

  const loadMoreRows = () => {
    const nextPage = page + 1;
    const newRows = filteredRows.slice(0, (nextPage + 1) * rowsPerPage);
    setDisplayedRows(newRows);
    setPage(nextPage);
  };

  const handleRowSelection = useCallback((rowId) => {
    setSelectedRows((prev) => {
      const newSelection = new Set(prev);
      if (newSelection.has(rowId)) {
        newSelection.delete(rowId);
        console.log(`Deselected row: ${rowId}`);
      } else {
        newSelection.add(rowId);
        console.log(`Selected row: ${rowId}`);
      }
      console.log('Current selections:', Array.from(newSelection));
      return newSelection;
    });
  }, []);

  const getMetaData = async (partnerId) => {
    if (!partnerId) {
      console.warn('No partnerId in getMetaData');
      toast.error('No partner selected. Please select a partner.', {
        position: 'bottom-right',
        autoClose: 5000,
      });
      setLoading(false);
      return;
    }
    console.log('Fetching metadata for partnerId:', partnerId);
    setLoading(true);
    const URL = `${GET_METADATA}?partnerId=${partnerId}`;
    try {
      const response = await getInstantData(URL);
      console.log('API Response:', JSON.stringify(response, null, 2)); // Log raw API response
      const channelData = Array.isArray(response?.data) ? response.data : [];
      const formattedData = channelData
        .filter((item, index) => {
          if (!item || typeof item !== 'object' || !item.programId) {
            console.warn(`Invalid item from API at index ${index}:`, item);
            return false;
          }
          return true;
        })
        .map((item, index) => {
          const formatTimestamp = (ts) => {
            if (!ts || isNaN(Number(ts))) return '';
            const date = new Date(Number(ts));
            if (isNaN(date.getTime())) return '';
            const year = date.getFullYear();
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            const day = date.getDate().toString().padStart(2, '0');
            return `${year}-${month}-${day}`;
          };

          // Match rows by id or tempId to preserve client-side data
          const existingRow = rows.find(row => (row.id && row.id === item.id) || (row.tempId && row.tempId === item.tempId));

          return {
            ...item,
            programId: item.programId || '',
            title: item.title || '',
            duration: /^[0-2][0-3]:[0-5][0-9]:[0-5][0-9]$/.test(item.duration) ? item.duration : '00:00:00',
            shortSynopsis: item.shortSynopsis || '',
            actors: item.actors || '',
            directors: item.directors || '',
            releaseDate: item.releaseDate ? formatTimestamp(item.releaseDate) : existingRow?.releaseDate || '',
            startDate: item.startDate ? formatTimestamp(item.startDate) : existingRow?.startDate || '',
            endDate: item.endDate ? formatTimestamp(item.endDate) : existingRow?.endDate || '',
            originalLanguage: item.originalLanguage || existingRow?.originalLanguage || null,
            origin: item.origin || existingRow?.origin || '',
            genres: Array.isArray(item.genres) && item.genres.length > 0 ? item.genres : existingRow?.genres || [],
            themes: Array.isArray(item.themes) && item.themes.length > 0 ? item.themes : existingRow?.themes || [],
            categories: Array.isArray(item.categories) && item.categories.length > 0 ? item.categories : existingRow?.categories || [],
            ratings: item.ratings || existingRow?.ratings || '',
            ratingsDescriptor: item.ratingsDescriptor || existingRow?.ratingsDescriptor || '',
            territories: Array.isArray(item.territories) && item.territories.length > 0 ? item.territories : existingRow?.territories || [],
            audioLanguages: Array.isArray(item.audioLanguages) && item.audioLanguages.length > 0 ? item.audioLanguages : existingRow?.audioLanguages || [],
            contentGroup: Array.isArray(item.contentGroup) ? item.contentGroup.join(',') : (existingRow?.contentGroup || item.contentGroup || ''),
            partnerId: parseInt(partnerId),
            tempId: item.tempId || `temp_${Date.now()}_${index}`,
          };
        });
      console.log('Formatted Data:', JSON.stringify(formattedData, null, 2)); // Log formatted data
      const unsavedRows = rows.filter(row => row.tempId && !row.id);
      console.log('Unsaved Rows:', JSON.stringify(unsavedRows, null, 2));
      setRows([...unsavedRows, ...formattedData]);
      setFilteredRows([...unsavedRows, ...formattedData]);
      setDisplayedRows([...unsavedRows, ...formattedData].slice(0, rowsPerPage));
      dispatch(MetaGridAction(formattedData));
    } catch (error) {
      console.error('Error fetching metadata for partnerId:', partnerId, error);
      toast.error('Failed to load metadata.', {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const parseDateToTimestamp = (dateValue) => {
    if (!dateValue || dateValue.trim() === '') {
      console.warn(`Empty or invalid date value: ${dateValue}`);
      return null;
    }
    let year, month, day;
    if (dateValue.includes('-')) {
      [year, month, day] = dateValue.split('-').map(Number);
    } else if (dateValue.includes('/')) {
      [day, month, year] = dateValue.split('/').map(Number);
    } else {
      console.warn(`Invalid date format: ${dateValue}`);
      return null;
    }
    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date parsed: ${dateValue}`);
      return null;
    }
    return date.getTime();
  };

  const updateIds = () => {
    if (!rows || !locationData?.state?.id) {
      console.warn('No rows or partnerId for updateIds');
      toast.error('Cannot update: No partner selected.', {
        position: 'bottom-right',
        autoClose: 5000,
      });
      return [];
    }

    console.log('Preparing metadata payload for partnerId:', locationData.state.id);

    const updatedData = rows
      .map((item, index) => {
        if (!item || typeof item !== 'object') {
          console.warn(`Skipping invalid row at index ${index}:`, item);
          return null;
        }

        // Helper function to resolve IDs for array fields
        const resolveIds = (items, list, fieldName) => {
          console.log(`Ritemsitemsitemsitems for ${fieldName}`, JSON.stringify(items), JSON.stringify(list));
          //  console.log(`Resolving  ${Array.isArray(items)} ${fieldName}:`,  JSON.stringify(list));
          // console.log(`Resolving IDs for field: ${fieldName}`);
          // console.log('Incoming items:', items);
          // console.log('List data:', list);
          if (Array.isArray(items)) {

            return items
              .map((item) => {
                if (typeof item === 'object' && list?.data && item?.name) {
                  console.log();


                  // return item.id ? Number(item.id) : null;
                  const found = list?.data?.find((l) => l?.name === item?.name)
                  return found?.id ? Number(found.id) : null;
                } else if (typeof item === 'object' && item?.name && fieldName === 'audioLanguages') {
                  // return item.id ? Number(item.id) : null;
                  // return item.id ? Number(item.id) : null;
                  const found = list?.find((l) => l?.name === item?.name)
                  return found?.id ? Number(found.id) : null;
                }

                if (typeof item === 'object' && item?.id) {
                  return item.id ? Number(item.id) : null;
                } else if (typeof item === 'string' && list) {
                  const found = list.find((l) => l.name === item);
                  return found?.id ? Number(found.id) : null;
                }

                return null;
              })
              .filter(Boolean);
          }
          return [];
        };
        const resolveIdsRating = (items, list, fieldName) => {
          // Log inputs for debugging
          console.log(`debuggingdebugging ${fieldName}:`," typeof items",  items, );
  console.log(`Resolving ${fieldName}:`, typeof items, items, typeof items === 'string' );


          // Check if items is an array and list is valid
          if (typeof items === 'string' && items ) {
            console.log(`typeoftypeoftypeoftypeof ${items}:`, items);
              const found = list?.find((l) => l?.name === items);
              return found?.id ?[ Number(found.id)] : null;
          }
      
          return [];
        };

        const resolveIdsTerritoryIds = (items, list, fieldName) => {
          if (Array.isArray(items)) {
            return items
              .map((item) => {

                if (typeof item === 'object' && item?.name) {
                  // return item.id ? Number(item.id) : null;
                  const found = list?.find((l) => l?.name === item?.name)
                  return found?.id ? Number(found.id) : null;

                }
                return null;
              })
              .filter(Boolean);
          }
          return [];
        };


        // Handle originalLanguageId
        const originalLangId = typeof item.originalLanguage === 'string'
          ? AudioLanguageList?.find((l) => l.name === item.originalLanguage)?.id || null
          : (typeof item.originalLanguage === 'object' && item.originalLanguage?.id
            ? Number(item.originalLanguage.id)
            : null);
        console.log('originalLangId', resolveIds(item.territories, CountryList, 'territories'))
        // Prepare the payload
        const payload = {
          id: item.id ? Number(item.id) : undefined,
          programId: item.programId || '',
          title: item.title || '',
          duration: item.duration || '00:00:00',
          shortSynopsis: item.shortSynopsis || '',
          actors: item.actors || '',
          directors: item.directors || '',
          // releaseDate: moment(item.releaseDate).format("YYYY/MM/DD") ,
          releaseDate: parseDateToTimestamp(item.releaseDate),
          startDate: parseDateToTimestamp(item.startDate),
          endDate: parseDateToTimestamp(item.endDate),
          originalLanguageId: originalLangId,
          origin: typeof item.origin === 'object' && item.origin?.name ? item.origin.name : item.origin || 'International',
          genreIds: resolveIds(item.genres, GenreList, 'genres'),
          themeIds: resolveIds(item.themes, ThemesList, 'themes'),
          categoryIds: resolveIds(item.categories, CategoriesList, 'categories'),
          audioLanguageIds: resolveIds(item.audioLanguages, AudioLanguageList, 'audioLanguages'),
          territoryIds: resolveIdsTerritoryIds(item.territories, CountryList, 'territories'),
          ratingIds: resolveIdsRating(item.ratings, MaturityList, 'ratings'),
          ratingsDescriptor: item.ratingsDescriptor || '',
          contentGroup: Array.isArray(item.contentGroup)
            ? item.contentGroup.join(',')
            : (item.contentGroup || ''),
          partnerId: Number(locationData.state.id),
          tempId: item.tempId || `temp_${Date.now()}_${index}`,
        };

        return payload;
      })
      .filter(Boolean);

    console.log('Final payload for POST_METADATA_BULK:', JSON.stringify(updatedData, null, 2));
    return updatedData;
  };

  const updateMetaData = async () => {
    setLoading(true);
    const URL = `${POST_METADATA_BULK}`;
    const updatedData = updateIds();
    if (!updatedData.length) {
      toast.error('No valid rows to update.', {
        position: 'bottom-right',
        autoClose: 5000,
      });
      setLoading(false);
      return;
    }
    console.log('Payload for POST_METADATA_BULK:', JSON.stringify(updatedData, null, 2));
    try {
      await postInstantData(URL, { movies: updatedData });
      setRows([]); // Clear rows to ensure fresh data
      setFilteredRows([]);
      setDisplayedRows([]);
      await getMetaData(locationData?.state?.id); // Refresh data from server
      setSelectedRows(new Set());
      setSelectedRow(null);
      toast.success('Metadata updated successfully', {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } catch (error) {
      console.error('Error updating metadata:', error.response?.data || error.message);
      toast.error(`Failed to update metadata: ${error.response?.data?.message || error.message}`, {
        position: 'bottom-right',
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  const addMetaData = () => {
    if (!locationData?.state?.id) {
      toast.error('Cannot add row: No partner selected.', {
        position: 'bottom-right',
        autoClose: 5000,
      });
      return;
    }
    const emptyRow = {
      programId: '',
      title: '',
      duration: '00:00:00',
      shortSynopsis: '',
      actors: '',
      directors: '',
      releaseDate: '',
      originalLanguage: null,
      origin: '',
      genres: [],
      themes: [],
      categories: [],
      ratings: '',
      ratingsDescriptor: '',
      territories: [],
      startDate: '',
      endDate: '',
      audioLanguages: [],
      contentGroup: [],
      partnerId: locationData.state.id,
      tempId: `temp_${Date.now()}`,
    };
    console.log('Adding new row:', emptyRow);
    setRows([emptyRow, ...rows.filter(row => row != null)]);
    setHighlightedRowId(emptyRow.tempId);
    setTimeout(() => {
      setHighlightedRowId(null);
    }, 3000);
  };

  const removeMetaData = async () => {
    if (selectedRows.size === 0 && !selectedRow?.id) {
      const newRows = rows.slice(1);
      setRows(newRows);
      setFilteredRows(newRows);
      setDisplayedRows(newRows);
      setSelectedRow(null);
      setSelectedRows(new Set());
      return;
    }

    setLoading(true);
    try {
      const deletePromises = Array.from(selectedRows).map((rowId) => {
        const row = rows.find((r) => r.id === rowId || r.tempId === rowId);
        if (row?.id) {
          const URL = `${Metadataupdate_URL}/${row.id}`;
          return deleteInstantData(URL);
        }
        return Promise.resolve();
      });

      await Promise.all(deletePromises);

      const updatedRows = rows.filter((row) => !selectedRows.has(row.id || row.tempId));
      setRows(updatedRows);
      setFilteredRows(updatedRows);
      setDisplayedRows(updatedRows.slice(0, (page + 1) * rowsPerPage));
      toast.success('Selected metadata removed successfully');
    } catch (error) {
      toast.error('Something went wrong');
    } finally {
      setSelectedRow(null);
      setSelectedRows(new Set());
      setLoading(false);
    }
  };

  const exportToExcel = () => {
    const dataWithHeaders = mapDataWithHeaders(filteredRows);
    const worksheet = XLSX.utils.json_to_sheet(dataWithHeaders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Metadata');
    XLSX.writeFile(workbook, 'metadata_export.xlsx');
  };


  const updateRow = (rowIdOrIndex, key, value) => {
    console.log(`Updating row with rowIdOrIndex: ${rowIdOrIndex}, key: ${key}, value:`, value);

    setRows((prevRows) => {
      const newRows = [...prevRows];
      let rowIndex = -1;

      if (typeof rowIdOrIndex === 'number' && rowIdOrIndex >= 0 && rowIdOrIndex < newRows.length) {
        rowIndex = rowIdOrIndex;
      } else {
        rowIndex = newRows.findIndex((row) => row.id === rowIdOrIndex || row.tempId === rowIdOrIndex);
      }

      if (rowIndex !== -1) {
        newRows[rowIndex] = { ...newRows[rowIndex], [key]: value };
        console.log('Updated rows state:', newRows);
      } else {
        console.warn(`Row with ID or index ${rowIdOrIndex} not found in rows`);
      }
      return newRows;
    });

    setDisplayedRows((prevDisplayed) => {
      const newDisplayed = [...prevDisplayed];
      let rowIndex = -1;

      if (typeof rowIdOrIndex === 'number' && rowIdOrIndex >= 0 && rowIdOrIndex < newDisplayed.length) {
        rowIndex = rowIdOrIndex;
      } else {
        rowIndex = newDisplayed.findIndex((row) => row.id === rowIdOrIndex || row.tempId === rowIdOrIndex);
      }

      if (rowIndex !== -1) {
        newDisplayed[rowIndex] = { ...newDisplayed[rowIndex], [key]: value };
        console.log('Updated displayedRows state:', newDisplayed);
      } else {
        console.warn(`Row with ID or index ${rowIdOrIndex} not found in displayedRows`);
      }
      return newDisplayed;
    });
  };

  const mapDataWithHeaders = (dataRows) => {
    return dataRows?.map((item) => {
      let ratingValue;
      if (Array.isArray(item.ratings)) {
        ratingValue = item.ratings.map((r) => r?.name || r).join(', ');
      } else if (typeof item.ratings === 'object' && item.ratings?.name) {
        ratingValue = item.ratings.name;
      } else {
        ratingValue = item.ratings || '';
      }

      const formatDateForExport = (date) => {
        if (!date) return '';
        const d = new Date(date);
        if (isNaN(d.getTime())) return '';
        const day = d.getDate().toString().padStart(2, '0');
        const month = (d.getMonth() + 1).toString().padStart(2, '0');
        const year = d.getFullYear();
        return `${day}/${month}/${year}`;
      };

      return {
        'Prog. ID': item?.programId || '',
        'Title': item?.title || '',
        'Duration': item?.duration || '',
        'Short Synopsis': item?.shortSynopsis || '',
        'Actors': item?.actors || '',
        'Directors': item?.directors || '',
        'Release Date': formatDateForExport(item?.releaseDate),
        'Original Language': item?.originalLanguage?.name || item?.originalLanguage || '',
        'Origin': item?.origin?.name || item?.origin || '',
        'Genre': item?.genres?.map((data) => data?.name).join(', ') || '',
        'Themes': item?.themes?.map((data) => data?.name).join(', ') || '',
        'Categories': item?.categories?.map((data) => data?.name).join(', ') || '',
        'Rating': ratingValue,
        'Ratings Descriptor': item?.ratingsDescriptor || '',
        'Territory': item?.territories?.map((data) => data?.name).join(', ') || '',
        'Start Date': formatDateForExport(item?.startDate),
        'End Date': formatDateForExport(item?.endDate),
        'Audio Language': item?.audioLanguages?.map((data) => data?.name).join(', ') || '',
        contentGroup: Array.isArray(item.contentGroup)
          ? item.contentGroup.join(', ')
          : item.contentGroup || '',
      };
    });
  };

 
  const columns = useMemo(
    () => getColumns({
      AudioLanguageList,
      CountryList,
      GenreList,
      ThemesList,
      CategoriesList,
      MaturityList,
      ContentGroup,
      updateRow,
    }),
    [AudioLanguageList, CountryList, GenreList, ThemesList, CategoriesList, MaturityList]
  );


  return (
    <div className="content-body" style={{ position: 'relative', overflowX: 'hidden' }}>
      <div className="dashboard-content">
        <div className="top-content">
          <h3>
            <a onClick={() => navigate(-1)}>
              <img src={backicon} alt="Back Icon" />
            </a>
            <span style={{ color: '#999999', marginLeft: 20, fontSize: 20, fontWeight: 'bold' }}>
              Metafile for {locationData?.state?.name}
            </span>
          </h3>
          {permission && (
            <div className="right-side flex">
              <SearchBar searchText={searchText} setSearchText={setSearchText} />
              <DeleteRowButton onClick={removeMetaData} disabled={selectedRows.size === 0} />
              <AddRowButton onClick={addMetaData} />
              <ExportButton onClick={exportToExcel} />
              <UpdateButton onClick={updateMetaData} />
            </div>
          )}
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : filteredRows.length === 0 ? (
          <NoDataMessage />
        ) : (
          <div className={styles.bulkGrid} onScroll={handleScroll} style={{ maxHeight: '100vh', overflowY: 'auto' }}>
            <div className={styles.scrollContent}>
              <Suspense fallback={<LoadingSpinner />}>
                <EditableTable
                  data={displayedRows.filter(row => row !== null && row !== undefined && row.programId !== undefined)}
                  columns={columns}
                  highlightedRowId={highlightedRowId}
                  selectedRows={selectedRows}
                  handleRowSelection={handleRowSelection}
                />
              </Suspense>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
}

export default EditableGrid;
